import { apiRequest, authStorage } from './api';

export async function fetchParrainageDashboard() {
  const response = await apiRequest('/parrainage/me', { method: 'GET', withAuth: true });
  return response?.data ?? null;
}

export async function fetchParrainageFilleuls(page = 1, limit = 10) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const response = await apiRequest(`/parrainage/me/filleuls?${params}`, {
    method: 'GET',
    withAuth: true,
  });
  return response?.data ?? { items: [], pagination: { page: 1, totalPages: 1, total: 0 } };
}

export async function transferParrainageWallet({ destinataireCodeParrainage, montant }) {
  const response = await apiRequest('/parrainage/me/transfer', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({
      destinataireCodeParrainage: String(destinataireCodeParrainage ?? '').trim().toUpperCase(),
      montant: Number(montant),
    }),
  });
  return response;
}

export async function fetchAdminParrainageOverview(page = 1) {
  const response = await apiRequest(`/admin/parrainage/overview?page=${page}`, {
    method: 'GET',
    withAuth: true,
  });
  return response?.data ?? null;
}

export async function resolvePublicReferralCode(codeOrPayload) {
  const q = encodeURIComponent(String(codeOrPayload ?? '').trim());
  const response = await apiRequest(`/parrainage/public/resolve?code=${q}`, { method: 'GET' });
  return response?.data ?? { valid: false, message: 'Code invalide' };
}

export async function updateUtilisateurTypeProfilParrain(userId, typeProfilParrain) {
  const response = await apiRequest(`/admin/utilisateurs/${userId}/type-parrain`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ typeProfilParrain: typeProfilParrain || null }),
  });
  return response;
}

function apiBaseForDownload() {
  const env = process.env.REACT_APP_API_URL;
  if (env) return env.replace(/\/$/, '');
  return '/api';
}

export function getAdminParrainageExportCsvUrl() {
  return `${apiBaseForDownload()}/admin/parrainage/export.csv`;
}

export function getAdminParrainageExportPdfUrl() {
  return `${apiBaseForDownload()}/admin/parrainage/export.pdf`;
}

async function downloadAdminExportBlob(path, fallbackName) {
  const baseUrl = apiBaseForDownload();
  const accessToken = authStorage.getAccessToken();
  const headers = new Headers();
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  const response = await fetch(`${baseUrl}${path}`, { method: 'GET', headers });
  if (!response.ok) {
    let message = 'Export impossible';
    try {
      const payload = await response.json();
      message = payload?.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fallbackName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadAdminParrainageCsv() {
  const date = new Date().toISOString().slice(0, 10);
  return downloadAdminExportBlob('/admin/parrainage/export.csv', `parrainage-${date}.csv`);
}

export async function downloadAdminParrainagePdf() {
  const date = new Date().toISOString().slice(0, 10);
  return downloadAdminExportBlob('/admin/parrainage/export.pdf', `parrainage-${date}.pdf`);
}

function publicWebOrigin() {
  return (
    (typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : process.env.REACT_APP_PUBLIC_URL) || 'http://localhost:3001'
  ).replace(/\/$/, '');
}

/** Lien / QR plateforme : ouvre la création de compte (sans code parrain). */
export function buildPlatformRegisterUrl() {
  return `${publicWebOrigin()}/inscription`;
}

export function buildReferralRegisterUrl(codeParrainage) {
  const params = new URLSearchParams({
    codeParrain: String(codeParrainage || '').trim().toUpperCase(),
  });
  return `${publicWebOrigin()}/inscription?${params.toString()}`;
}

export function buildReferralQrImageUrl(registerUrl, size = 220) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&ecc=H&data=${encodeURIComponent(registerUrl)}`;
}

function logoPublicUrl() {
  const base = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  return `${base}/jconsult-academia-logo.png`;
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image introuvable'));
    img.src = src;
  });
}

/** QR PNG avec logo JCONSULT au centre (affichage, partage, téléchargement). */
export async function composeReferralQrImageBlob(registerUrl, size = 512) {
  const qrImageUrl = buildReferralQrImageUrl(registerUrl, size);
  const [qrImg, logoImg] = await Promise.all([loadImageElement(qrImageUrl), loadImageElement(logoPublicUrl())]);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas indisponible');
  }

  ctx.drawImage(qrImg, 0, 0, size, size);

  const logoSize = Math.max(56, Math.round(size * 0.22));
  const pad = 8;
  const x = (size - logoSize) / 2;
  const y = (size - logoSize) / 2;

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  const r = 14;
  const w = logoSize + pad * 2;
  const h = logoSize + pad * 2;
  const left = x - pad;
  const top = y - pad;
  ctx.moveTo(left + r, top);
  ctx.arcTo(left + w, top, left + w, top + h, r);
  ctx.arcTo(left + w, top + h, left, top + h, r);
  ctx.arcTo(left, top + h, left, top, r);
  ctx.arcTo(left, top, left + w, top, r);
  ctx.closePath();
  ctx.fill();

  ctx.drawImage(logoImg, x, y, logoSize, logoSize);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Export QR impossible'));
    }, 'image/png');
  });
}

export async function fetchReferralQrImageBlob(registerUrl, size = 512) {
  try {
    return await composeReferralQrImageBlob(registerUrl, size);
  } catch {
    const qrImageUrl = buildReferralQrImageUrl(registerUrl, size);
    const response = await fetch(qrImageUrl);
    if (!response.ok) {
      throw new Error('Impossible de charger le QR code.');
    }
    return response.blob();
  }
}

/** Partage le QR en image (fichier) comme le lien natif quand le navigateur le permet. */
export async function shareReferralQrImage({ registerUrl, codeParrainage }) {
  const code = String(codeParrainage || '').trim().toUpperCase();
  const blob = await composeReferralQrImageBlob(registerUrl, 512);
  const file = new File([blob], `qr-parrainage-${code}.png`, { type: 'image/png' });
  const message = `Rejoignez JCONSULT MY Academia avec mon code parrain ${code}.\n${registerUrl}`;

  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({
      title: 'Mon QR parrainage JCONSULT MY Academia',
      text: message,
      files: [file],
    });
    return { mode: 'share-file' };
  }

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Mon QR parrainage',
        text: `${message}\n(Le QR a été téléchargé sur votre appareil.)`,
        url: registerUrl,
      });
    } catch {
      /* annulé */
    }
  }

  downloadReferralQrBlob(blob, code);
  return { mode: 'download' };
}

export function downloadReferralQrBlob(blob, codeParrainage) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `qr-parrainage-${String(codeParrainage || 'code').trim().toUpperCase()}.png`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadReferralQrImage(registerUrl, codeParrainage) {
  const blob = await composeReferralQrImageBlob(registerUrl, 512);
  downloadReferralQrBlob(blob, codeParrainage);
}

export function downloadPlatformQrBlob(blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'qr-inscription-jconsult-academia.png';
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadPlatformQrImage(registerUrl) {
  const blob = await composeReferralQrImageBlob(registerUrl ?? buildPlatformRegisterUrl(), 512);
  downloadPlatformQrBlob(blob);
}

export async function sharePlatformQrImage({ registerUrl }) {
  const url = registerUrl ?? buildPlatformRegisterUrl();
  const blob = await composeReferralQrImageBlob(url, 512);
  const file = new File([blob], 'qr-inscription-jconsult-academia.png', { type: 'image/png' });
  const message = `Créez votre compte sur JCONSULT MY Academia.\n${url}`;

  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({
      title: 'Inscription JCONSULT MY Academia',
      text: message,
      files: [file],
    });
    return { mode: 'share-file' };
  }

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Inscription JCONSULT MY Academia',
        text: `${message}\n(Le QR a été téléchargé sur votre appareil.)`,
        url,
      });
    } catch {
      /* annulé */
    }
  }

  downloadPlatformQrBlob(blob);
  return { mode: 'download' };
}
